import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Building, Building2, Home, Users, ArrowLeftRight, BookOpen, Wallet, Landmark, KeyRound, LogOut, Eye, History, CheckSquare, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

const NAV_SECTIONS = [
    {
        title: null,
        items: [{ to: "/", icon: LayoutDashboard, label: "Cockpit", testid: "nav-dashboard", end: true }],
    },
    {
        title: "Bestand",
        items: [
            { to: "/entitaeten", icon: Building, label: "Entitäten", testid: "nav-entitaeten" },
            { to: "/liegenschaften", icon: Landmark, label: "Liegenschaften", testid: "nav-liegenschaften" },
            { to: "/immobilien", icon: Building2, label: "Immobilien", testid: "nav-immobilien" },
            { to: "/einheiten", icon: Home, label: "Mieteinheiten", testid: "nav-einheiten" },
            { to: "/mieter", icon: Users, label: "Mieter", testid: "nav-mieter" },
        ],
    },
    {
        title: "Prozesse",
        items: [
            { to: "/aufgaben", icon: CheckSquare, label: "Aufgaben", testid: "nav-aufgaben" },
            { to: "/widersprueche", icon: ShieldAlert, label: "Widersprüche", testid: "nav-widersprueche" },
            { to: "/wechsel", icon: ArrowLeftRight, label: "Mieterwechsel", testid: "nav-wechsel" },
            { to: "/mietkonto", icon: Wallet, label: "Mietkonto", testid: "nav-mietkonto" },
            { to: "/konten", icon: Landmark, label: "Bankkonten", testid: "nav-konten" },
        ],
    },
    {
        title: "Wissen",
        items: [{ to: "/wiki", icon: BookOpen, label: "Wiki", testid: "nav-wiki" }],
    },
    {
        title: "Verwaltung",
        items: [
            { to: "/zugaenge", icon: KeyRound, label: "Zugänge", testid: "nav-zugaenge" },
            { to: "/historie", icon: History, label: "Historie", testid: "nav-historie" },
        ],
    },
];

const NAV = NAV_SECTIONS.flatMap((s) => s.items);

const Sidebar = () => (
    <aside className="hidden md:flex md:w-64 flex-col border-r border-gray-200 bg-white shrink-0" data-testid="sidebar">
        <div className="px-6 h-16 flex items-center gap-3 border-b border-gray-200">
            <div className="w-9 h-9 rounded-md bg-white border border-gray-200 grid place-items-center overflow-hidden">
                <img src="/tplex-logo.jpg" alt="Tplex" className="w-7 h-7 object-contain" />
            </div>
            <div className="leading-tight">
                <div className="font-heading font-bold text-[15px] text-slate-900">Tplex</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Mieterwechsel</div>
            </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-4">
            {NAV_SECTIONS.map((section, si) => (
                <div key={si}>
                    {section.title && (
                        <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold">
                            {section.title}
                        </div>
                    )}
                    <div className="space-y-1">
                        {section.items.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={n.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-slate-900 text-white"
                                            : "text-slate-600 hover:bg-gray-100 hover:text-slate-900"
                                    }`
                                }
                            >
                                <n.icon size={16} strokeWidth={2} />
                                <span className="font-medium">{n.label}</span>
                            </NavLink>
                        ))}
                    </div>
                </div>
            ))}
        </nav>
        <div className="px-4 py-4 border-t border-gray-200">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">Plattform</div>
            <div className="text-xs text-slate-600 leading-relaxed">
                Verwaltung von Entitäten, Immobilien und Mieterwechseln —<br />
                <span className="text-slate-400">eine Plattform, alle Mandanten.</span>
            </div>
        </div>
    </aside>
);

const TopHeader = () => {
    const { pathname } = useLocation();
    const current = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to))) ?? { label: "Mieterwechsel" };
    const { nutzer, rolle, nurLesen, abmelden } = useAuth();
    // Initialen aus dem Namen, sonst aus der Adresse. Vorher stand hier fest
    // "SM" — ein Platzhalter, der mit zwei Nutzern zur Falschauskunft wird.
    const initialen = (nutzer?.name || nutzer?.email || "?")
        .split(/[\s._@-]+/).filter(Boolean).slice(0, 2)
        .map((t) => t[0].toUpperCase()).join("") || "?";
    return (
        <header className="h-16 flex items-center justify-between px-6 md:px-10 border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-20" data-testid="top-header">
            <div className="flex items-center gap-3">
                <Home size={16} className="text-gray-400" />
                <span className="font-heading font-semibold text-slate-900 text-sm" data-testid="breadcrumb-current">{current.label}</span>
            </div>
            <div className="flex items-center gap-3">
                {nurLesen && (
                    <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-200 bg-amber-50 text-[11px] font-medium text-amber-800"
                         title="Dein Zugang darf ansehen, aber nichts ändern. Schreibende Knöpfe sind deshalb gesperrt."
                         data-testid="badge-nur-lesen">
                        <Eye size={12} /> Nur Lesen
                    </div>
                )}
                <div className="hidden sm:block text-right leading-tight">
                    <div className="text-xs font-medium text-slate-700" data-testid="header-nutzer">
                        {nutzer?.name || nutzer?.email}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400">{rolle}</div>
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-semibold"
                     title={nutzer?.email}>
                    {initialen}
                </div>
                <button onClick={abmelden}
                        className="p-2 rounded-md text-slate-500 hover:bg-gray-100 hover:text-slate-900 transition-colors"
                        title="Abmelden" data-testid="btn-abmelden">
                    <LogOut size={16} />
                </button>
            </div>
        </header>
    );
};

const Layout = ({ children }) => (
    <div className="flex min-h-screen bg-white">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
            <TopHeader />
            <main className="flex-1 overflow-y-auto bg-[#FAFAFA]">
                <div className="animate-rise">{children}</div>
            </main>
        </div>
        <Toaster />
    </div>
);

export default Layout;
